import { useMemo, useCallback } from "react";
import { useQueries, UseQueryResult } from "@tanstack/react-query";
import { getHomesites } from "../../api/homesites/homesites.api";
import { getCommunities } from "../../api/communities/communities.api";
import { Combined, HomeSitesDataIndex } from "./types";
import { IHomesite } from "../../api/homesites/homesites.types";
import { Communities, Community } from "../../api/communities/types";


export const useHomeSiteDataIndexQuery = (divisionCode: string) => {

     const queries = useMemo(
          () => [
               {
                    queryKey: ["homeSites", divisionCode],
                    enabled: !!divisionCode,
                    queryFn: () => getHomesites(divisionCode),
                    staleTime: 1000 * 60 * 15, // 15 minutes
                    gcTime: 1000 * 60 * 15, // 15 minutes
                    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
                    refetchIntervalInBackground: true,
               },
               {
                    queryKey: ["communities", divisionCode],
                    enabled: !!divisionCode,
                    queryFn: () => getCommunities({ filter: `divisionCode=${divisionCode},status=active` }),
                    staleTime: 1000 * 60 * 15, // 15 minutes
                    gcTime: 1000 * 60 * 15, // 15 minutes
                    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
                    refetchIntervalInBackground: true,
               },
          ],
          [divisionCode]
     );

     const combine = useCallback(
          (idx: UseQueryResult<IHomesite[] | Communities, Error>[]): Combined => {
               const homesites = (idx[0]?.data as IHomesite[] | undefined) ?? [];
               const communities = (idx[1]?.data as Communities | undefined)?.data ?? [];

               const compositeKey = (code: string, phase: string) => `${code}-${phase}`;
               const communityByKey = new Map<string, Community>();
               for (const c of communities) {
                    const key = compositeKey(c.communityCode, c.communityPhase);
                    communityByKey.set(key, c);
               }

               const data: HomeSitesDataIndex[] = homesites.map((hs) => {
                    const key = compositeKey(hs.communityCode, hs.communityPhase);
                    const community = communityByKey.get(key);
                    return HomeSitesDataIndex.createHomeSiteDataIndex(hs, community);
               });
     
               return {
                    data,
                    communities,
                    pending: idx.some((r) => r.isPending),
                    error: idx.find((r) => r.error)?.error,
                    isSuccess: idx.every((r) => r.isSuccess),
               };
          },
          []
     );


     const results = useQueries({ queries, combine });

     return results;
};
